import { Component } from '@theme/component';

/**
 * A tab set inside a theme drawer.
 *
 * Implements the ARIA tabs pattern with manual activation: arrow keys move
 * focus between tabs and the shopper presses Enter or Space to switch panel.
 * Manual activation is the right choice here because a panel can hold a size
 * chart or a set of model measurements — auto-activating on arrow key would
 * swap that content out from under someone arrowing past it.
 *
 * The markup is rendered server-side by `drawer-tabs.liquid`, already carrying
 * correct `role`, `aria-selected`, `aria-controls` and `hidden` attributes. So
 * the first tab is usable before this module loads, and this component only
 * takes over the switching.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist - The element carrying `role="tablist"`.
 *
 * @extends {Component<Refs>}
 */
export class DrawerTabs extends Component {
  requiredRefs = ['tablist'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.tablist.addEventListener('click', this.#onClick);
    this.refs.tablist.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.tablist.removeEventListener('click', this.#onClick);
    this.refs.tablist.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * @returns {HTMLButtonElement[]} The tab buttons, in document order.
   */
  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.refs.tablist.querySelectorAll('[role="tab"]')));
  }

  /**
   * @param {MouseEvent} event - The click event.
   */
  #onClick = (event) => {
    const tab = /** @type {HTMLElement | null} */ (event.target)?.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement)) return;

    this.select(tab);
  };

  /**
   * Moves focus with the arrow keys, Home and End. Focus wraps at both ends,
   * which the ARIA pattern calls for so a shopper can reach the last tab from
   * the first without traversing the set.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  #onKeyDown = (event) => {
    const tabs = this.#tabs;
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (current === -1) return;

    let next = current;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    // The drawer closes on Escape and scrolls on arrow keys; claiming these
    // keys here stops the panel scrolling while the shopper moves between tabs.
    event.preventDefault();
    tabs[next]?.focus();
  };

  /**
   * Shows the panel belonging to `tab` and hides the rest.
   *
   * Roving tabindex: only the selected tab is in the tab order, so Tab moves
   * out of the tab set rather than through every tab in it.
   *
   * @param {HTMLButtonElement} tab - The tab to select.
   */
  select(tab) {
    for (const candidate of this.#tabs) {
      const isSelected = candidate === tab;
      const panel = this.#panelFor(candidate);

      candidate.setAttribute('aria-selected', String(isSelected));
      candidate.tabIndex = isSelected ? 0 : -1;

      if (panel) panel.hidden = !isSelected;
    }

    this.dispatchEvent(new TabSelectEvent(tab.dataset.tabKey ?? ''));
  }

  /**
   * Selects a tab by its `key`, ignoring keys that are not in this set.
   *
   * The entry point for callers outside the tab set — see the delegate at the
   * foot of this file.
   *
   * @param {string} key - The `key` given to the `drawer-tab` render.
   */
  selectByKey(key) {
    const tab = this.#tabs.find((candidate) => candidate.dataset.tabKey === key);
    if (tab) this.select(tab);
  }

  /**
   * @param {HTMLButtonElement} tab - The tab whose panel is wanted.
   * @returns {HTMLElement | null} The panel the tab controls.
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    if (!id) return null;

    // Scoped to this component so two tab sets in one drawer cannot
    // cross-wire through a duplicated id.
    return this.querySelector(`#${CSS.escape(id)}`);
  }
}

if (!customElements.get('drawer-tabs')) {
  customElements.define('drawer-tabs', DrawerTabs);
}

/**
 * Lets a control elsewhere on the page open a drawer straight to one of its
 * tabs — the "Size guide" link in the product form opening the fit drawer on
 * its size guide rather than its first tab.
 *
 * Markup: `data-drawer-tab="<drawer-id>:<tab-key>"`. The control still opens
 * the drawer itself, through Horizon's `on:click="#<drawer-id>/open"`; this
 * only decides which tab is showing when it gets there. Keeping the two
 * separate means the control works as a plain drawer trigger even if the tab
 * key is later renamed or removed.
 *
 * Delegated from the document so it covers controls rendered after load, such
 * as a variant picker replaced by the Section Rendering API.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-drawer-tab]') : null;
  if (!trigger) return;

  const [drawerId, key] = (trigger.getAttribute('data-drawer-tab') ?? '').split(':');
  if (!drawerId || !key) return;

  const tabs = document.querySelector(`#${CSS.escape(drawerId)} drawer-tabs`);
  if (tabs instanceof DrawerTabs) tabs.selectByKey(key);
});

export class TabSelectEvent extends CustomEvent {
  /**
   * @param {string} key - The `key` of the newly selected tab.
   */
  constructor(key) {
    super(TabSelectEvent.eventName, { bubbles: true, detail: { key } });
  }

  static eventName = 'drawer-tabs:select';
}
